import { FC, useEffect, useState } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { useNavigate, useParams } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";
import { Box, Button, Divider, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  adminPutNotificationReqDtoSchema,
  useAdminNotificationControllerGetOne,
  useAdminNotificationControllerSend,
  useAdminNotificationControllerUpdate,
} from "service";
import {
  AdminNotificationResDto,
  AdminPutNotificationReqDto,
} from "service/api/estabrookSchemas";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import NotificationForm from "./notification-form";
import { omitBy, isNull } from "lodash";
import { useDialogStore } from "../../../state/dialog-store";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import TransitionRoutesPhaseModal from "../route-planner/transition-routes-modal";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import { queryClient } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";

const EditNotificationPage: FC = () => {
  const { notificationId } = useParams<{
    notificationId: string;
  }>();
  const styles = useStyles();
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();
  const [redirectRoute, setRedirectRoute] = useState<string | undefined>(
    undefined,
  );
  const [notificationSentRecipients, setNotificationSentRecipients] = useState<
    number | undefined
  >(undefined);
  const [shouldSendAfterSave, setShouldSendAfterSave] = useState(false);

  const { mutate, isLoading } = useAdminNotificationControllerUpdate({
    onSuccess: () => {
      resetForm();

      // Invalidate both the list and individual notification queries
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/notifications",
          operationId: "adminNotificationControllerGetAll",
          variables: {},
        }),
      );

      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/notifications/{notificationId}",
          operationId: "adminNotificationControllerGetOne",
          variables: { pathParams: { notificationId: notificationId! } },
        }),
      );

      // If we should send after save, send the notification
      if (shouldSendAfterSave) {
        setShouldSendAfterSave(false);
        sendNotification(
          { pathParams: { notificationId: notificationId! } },
          {
            onSuccess: () => {
              toast.success("Notification Updated and Sent Successfully");
            },
          },
        );
      } else {
        toast.success("Notification Updated Successfully");
      }

      setTimeout(() => {
        if (redirectRoute) {
          navigate(redirectRoute);
        }
      }, 10);
    },
  });

  const { data, isLoading: isDataLoading } =
    useAdminNotificationControllerGetOne({
      pathParams: { notificationId: notificationId || "" },
    });

  const { mutate: sendNotification } = useAdminNotificationControllerSend({
    onSuccess: (data) => {
      setNotificationSentRecipients(data.data?.recipientsCount);
    },
  });

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    setFieldValue,
    setFieldTouched,
    resetForm,
  } = useFormik<AdminPutNotificationReqDto>({
    initialValues: {
      title: "",
      description: "",
      type: [],
      deliveryType: "instant",
      recurrence: "daily",
      customPeriod: "day",
      customInterval: 1,
      customWeekday: [],
    },
    validationSchema: toFormikValidationSchema(
      adminPutNotificationReqDtoSchema,
    ),
    validate: toFormikValidate(adminPutNotificationReqDtoSchema),
    onSubmit: () => {
      mutate({
        body: { ...values },
        pathParams: { notificationId: notificationId! },
      });
    },
  });

  useEffect(() => {
    if (!isDataLoading && data) {
      const filteredData = omitBy(data.data, isNull) as AdminNotificationResDto;
      if (filteredData.endDate) {
        filteredData.endDate = filteredData.endDate.slice(0, 10);
      }
      resetForm({
        values: filteredData,
      });
    }
  }, [data, isDataLoading]);

  useUnsavedChangesBlocker(dirty);

  const onSendPress = async (notificationId: string) => {
    if (!isValid) {
      toast.error("Please fix form errors before sending");
      return;
    }

    if (!dirty) {
      sendNotification(
        { pathParams: { notificationId } },
        {
          onSuccess: () => {
            toast.success("Notification Sent Successfully");
            navigate(AdRoutes.notifications);
          },
        },
      );
      return;
    }

    setDialog({
      title: "Send Notification",
      description: `You have unsaved changes. Do you want to save and send this notification now?`,
      confirmText: "Save & Send",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        setShouldSendAfterSave(true);
        mutate({
          body: { ...values },
          pathParams: { notificationId: notificationId! },
        });
        onClose();
      },
    });
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Notification Center",
                url: AdRoutes.notifications,
              },
              {
                label: "Edit Notification",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || !dirty || !isValid}
              variant={
                values.deliveryType === "instant" ? "outlined" : "contained"
              }
              onClick={() => {
                setRedirectRoute(AdRoutes.notifications);
                submitForm();
              }}
              data-testid="save-notification-button"
            >
              {values.deliveryType === "instant" && "Save for Later"}
              {values.deliveryType === "recurring" && "Schedule Recurring"}
              {values.deliveryType === "scheduled" && "Schedule"}
            </Button>
            {values.deliveryType === "instant" && (
              <Button
                disabled={isLoading || data?.data?.recipientCount === 0}
                variant="contained"
                onClick={() => {
                  onSendPress(notificationId!);
                }}
              >
                Send Now
              </Button>
            )}
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <NotificationForm
        values={values}
        handleChange={handleChange}
        handleBlur={handleBlur}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
      />
      <Divider sx={{ mt: "40px" }} />
      <Box sx={styles.customerDetailContainer}>
        <Box>
          <Typography fontWeight={600}>Recipients</Typography>
          {data?.data && (
            <Typography variant="subtitle2" color="text.secondary">
              {data?.data?.recipientCount}
              {data?.data?.recipientCount && data?.data?.recipientCount > 1
                ? " recipients"
                : " recipient"}
              {` selected`}
            </Typography>
          )}
        </Box>
        <Box>
          <Button
            disabled={isLoading || !isValid}
            variant="outlined"
            onClick={() => {
              const route = AdRoutes.notificationRecipients.replace(
                ":notificationId",
                notificationId || "",
              );
              setRedirectRoute(route);
              if (dirty) {
                submitForm();
              } else {
                navigate(route);
              }
            }}
          >
            Manage Recipients
          </Button>
        </Box>
      </Box>
      <TransitionRoutesPhaseModal
        open={notificationSentRecipients !== undefined}
        title={"Notification sent"}
        description={`Notification sent to ${notificationSentRecipients} recipients.`}
        sx={styles.creatingRoutesDialogWidth}
        onClose={() => {
          setNotificationSentRecipients(undefined);
        }}
      />
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  customerDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    maxWidth: "50%",
    alignItems: "center",
  },
  flexContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
  },
  dayCheckbox: {
    position: "relative",
    minWidth: "38px",
    "&:before": {
      //zIndex: -1,
      position: "absolute",
      content: "''",
      display: "inline-block",
      top: "1px",
      width: "38px",
      height: "38px",
      borderRadius: "100px",
      border: "1px solid #8B9198",
    },
    "&.Mui-checked": {
      "&:before": {
        backgroundColor: "#004b3b40",
        borderColor: "#005140",
      },
    },
  },
  dialog: {
    ".MuiDialog-paper": {
      minWidth: "500px",
    },
  },
}));

export default EditNotificationPage;
